import { useCallback, useMemo, useState } from 'react';

type UseHoverOptions = {
  /** Side effect on enter, e.g. telling a parent which card is hovered. */
  onEnter?: () => void;
  /** Side effect on leave. */
  onLeave?: () => void;
};

type UseHover = {
  isHovered: boolean;
  /** Spread onto the element whose hover state you want. */
  hoverProps: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  };
};

/**
 * Tracks whether the pointer is over an element.
 *
 * Replaces eight separate implementations of the same two-line state plus
 * enter/leave handler pair, which had drifted into four different names
 * (`isHovered`, `showDescription`, `showTooltip`, `isDateHovered`).
 *
 * Returns props rather than a ref because every call site is a React element,
 * where spreading is less ceremony than attaching listeners.
 */
export function useHover({ onEnter, onLeave }: UseHoverOptions = {}): UseHover {
  const [isHovered, setIsHovered] = useState(false);

  const handleEnter = useCallback(() => {
    setIsHovered(true);
    onEnter?.();
  }, [onEnter]);

  const handleLeave = useCallback(() => {
    setIsHovered(false);
    onLeave?.();
  }, [onLeave]);

  const hoverProps = useMemo(
    () => ({ onMouseEnter: handleEnter, onMouseLeave: handleLeave }),
    [handleEnter, handleLeave]
  );

  return { isHovered, hoverProps };
}

export default useHover;
